<template>
  <div class="execution-detail-container">
    <div class="detail-header">
      <div class="header-left">
        <router-link to="/executions" class="back-link">
          <i class="fas fa-arrow-left"></i> Voltar para Execuções
        </router-link>
        <h1>Execução #{{ execution?.id }}</h1>
      </div>
      <div class="header-right">
        <span class="status-badge" :class="'status-' + execution?.status?.toLowerCase()">
          {{ execution?.status }}
        </span>
        <button 
          v-if="execution?.status === 'RUNNING' || execution?.status === 'PENDING'"
          @click="cancelExecution" 
          class="btn btn-danger"
        >
          <i class="fas fa-stop"></i> Cancelar Execução
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Carregando detalhes da execução...</p>
    </div>

    <div v-else-if="!execution" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-exclamation-triangle"></i>
      </div>
      <h2>Execução não encontrada</h2>
      <p>A execução solicitada não existe ou foi removida.</p>
      <router-link to="/executions" class="btn btn-primary">
        Ver Todas Execuções
      </router-link>
    </div>

    <div v-else class="execution-details">
      <div class="detail-cards">
        <div class="detail-card">
          <div class="card-header">
            <i class="fas fa-project-diagram"></i>
            <h3>Pipeline</h3>
          </div>
          <div class="card-body">
            <p>{{ execution.pipeline_name }}</p>
            <router-link :to="`/pipelines/${execution.pipeline_id}`" class="card-link">
              Ver Pipeline
            </router-link>
          </div>
        </div>

        <div class="detail-card">
          <div class="card-header">
            <i class="fas fa-clock"></i>
            <h3>Tempo</h3>
          </div>
          <div class="card-body">
            <div class="time-info">
              <span class="time-label">Início:</span>
              <span class="time-value">{{ formatDate(execution.start_time) }}</span>
            </div>
            <div class="time-info">
              <span class="time-label">Fim:</span>
              <span class="time-value">{{ formatDate(execution.end_time) }}</span>
            </div>
            <div class="time-info">
              <span class="time-label">Duração:</span>
              <span class="time-value">{{ calculateDuration(execution) }}</span>
            </div>
          </div>
        </div>

        <div class="detail-card">
          <div class="card-header">
            <i class="fas fa-info-circle"></i>
            <h3>Informações</h3>
          </div>
          <div class="card-body">
            <div class="info-item">
              <span class="info-label">ID:</span>
              <span class="info-value">{{ execution.id }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Iniciado por:</span>
              <span class="info-value">{{ execution.created_by || 'Sistema' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Worker:</span>
              <span class="info-value">{{ execution.worker_id || 'N/A' }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="execution-logs">
        <div class="logs-header">
          <h2>Logs de Execução</h2>
          <button @click="refreshLogs" class="btn btn-outline refresh-btn">
            <i class="fas fa-sync-alt"></i> Atualizar
          </button>
        </div>

        <div v-if="logsLoading" class="logs-loading">
          <div class="loading-spinner"></div>
          <p>Carregando logs...</p>
        </div>

        <div v-else-if="logs.length === 0" class="logs-empty">
          <p>Nenhum log disponível para esta execução.</p>
        </div>

        <div v-else class="logs-container">
          <div v-for="(log, index) in logs" :key="index" class="log-entry" :class="'log-level-' + log.level.toLowerCase()">
            <div class="log-timestamp">{{ formatLogTime(log.timestamp) }}</div>
            <div class="log-level">{{ log.level }}</div>
            <div class="log-module">{{ log.module_name || 'Sistema' }}</div>
            <div class="log-message">{{ log.message }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExecutionDetail',
  data() {
    return {
      loading: false,
      logsLoading: false,
      logs: [],
      refreshInterval: null
    }
  },
  computed: {
    execution() {
      return this.$store.getters.getCurrentExecution
    },
    executionId() {
      return this.$route.params.id
    }
  },
  methods: {
    async fetchExecution() {
      this.loading = true
      try {
        await this.$store.dispatch('fetchExecution', this.executionId)
      } catch (error) {
        console.error('Erro ao buscar execução:', error)
        this.$toast.error('Erro ao carregar detalhes da execução')
      } finally {
        this.loading = false
      }
    },
    
    async fetchLogs() {
      this.logsLoading = true
      try {
        const response = await axios.get(`${API_URL}/executions/${this.executionId}/logs`)
        this.logs = response.data
      } catch (error) {
        console.error('Erro ao buscar logs:', error)
        this.$toast.error('Erro ao carregar logs da execução')
      } finally {
        this.logsLoading = false
      }
    },
    
    refreshLogs() {
      this.fetchLogs()
    },
    
    async cancelExecution() {
      try {
        const success = await this.$store.dispatch('cancelExecution', this.executionId)
        if (success) {
          this.$toast.success('Execução cancelada com sucesso!')
          this.fetchExecution()
        } else {
          this.$toast.error(this.$store.getters.getError || 'Erro ao cancelar execução')
        }
      } catch (error) {
        console.error('Erro ao cancelar execução:', error)
        this.$toast.error('Erro ao cancelar execução')
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('pt-BR')
    },
    
    formatLogTime(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleTimeString('pt-BR')
    },
    
    calculateDuration(execution) {
      if (!execution.start_time) return '-'
      
      const start = new Date(execution.start_time)
      const end = execution.end_time ? new Date(execution.end_time) : new Date()
      
      const durationMs = end - start
      
      if (durationMs < 1000) {
        return `${durationMs}ms`
      } else if (durationMs < 60000) {
        return `${Math.floor(durationMs / 1000)}s`
      } else if (durationMs < 3600000) {
        const minutes = Math.floor(durationMs / 60000)
        const seconds = Math.floor((durationMs % 60000) / 1000)
        return `${minutes}m ${seconds}s`
      } else {
        const hours = Math.floor(durationMs / 3600000)
        const minutes = Math.floor((durationMs % 3600000) / 60000)
        return `${hours}h ${minutes}m`
      }
    },
    
    setupAutoRefresh() {
      // Atualizar a execução e logs a cada 5 segundos se estiver em andamento
      this.refreshInterval = setInterval(() => {
        if (this.execution && (this.execution.status === 'RUNNING' || this.execution.status === 'PENDING')) {
          this.fetchExecution()
          this.fetchLogs()
        }
      }, 5000)
    }
  },
  created() {
    this.fetchExecution()
    this.fetchLogs()
    this.setupAutoRefresh()
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  }
}
</script>

<style scoped>
.execution-detail-container {
  padding: 2rem;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header-left {
  display: flex;
  flex-direction: column;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  text-decoration: none;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.back-link:hover {
  color: #475569;
}

.header-left h1 {
  margin: 0;
  color: #1e293b;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.status-badge {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-completed {
  background-color: #dcfce7;
  color: #166534;
}

.status-running {
  background-color: #dbeafe;
  color: #1e40af;
}

.status-pending {
  background-color: #fef3c7;
  color: #92400e;
}

.status-failed {
  background-color: #fee2e2;
  color: #b91c1c;
}

.status-cancelled {
  background-color: #f3f4f6;
  color: #4b5563;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background-color: #2563eb;
}

.btn-outline {
  background-color: transparent;
  border: 1px solid #cbd5e1;
  color: #64748b;
}

.btn-outline:hover {
  border-color: #94a3b8;
  color: #475569;
}

.btn-danger {
  background-color: #ef4444;
  color: white;
}

.btn-danger:hover {
  background-color: #dc2626;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  text-align: center;
}

.empty-icon {
  font-size: 3rem;
  color: #cbd5e1;
  margin-bottom: 1rem;
}

.empty-state h2 {
  margin: 0 0 0.5rem 0;
  color: #1e293b;
}

.empty-state p {
  margin: 0 0 1.5rem 0;
  color: #64748b;
  max-width: 400px;
}

.execution-details {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.detail-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.detail-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  padding: 1rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-header i {
  color: #3b82f6;
  font-size: 1.25rem;
}

.card-header h3 {
  margin: 0;
  color: #1e293b;
  font-size: 1rem;
}

.card-body {
  padding: 1rem;
}

.card-link {
  display: inline-block;
  margin-top: 0.5rem;
  color: #3b82f6;
  text-decoration: none;
  font-size: 0.875rem;
}

.card-link:hover {
  text-decoration: underline;
}

.time-info, .info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.time-info:last-child, .info-item:last-child {
  margin-bottom: 0;
}

.time-label, .info-label {
  color: #64748b;
  font-size: 0.875rem;
}

.time-value, .info-value {
  color: #1e293b;
  font-size: 0.875rem;
  font-weight: 500;
}

.execution-logs {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.logs-header {
  padding: 1rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logs-header h2 {
  margin: 0;
  color: #1e293b;
  font-size: 1.25rem;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.logs-loading, .logs-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #64748b;
}

.logs-container {
  padding: 1rem;
  max-height: 400px;
  overflow-y: auto;
  font-family: monospace;
  font-size: 0.875rem;
}

.log-entry {
  display: grid;
  grid-template-columns: 100px 80px 150px 1fr;
  gap: 1rem;
  padding: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.log-entry:last-child {
  border-bottom: none;
}

.log-timestamp {
  color: #64748b;
}

.log-level {
  font-weight: 600;
}

.log-module {
  color: #475569;
}

.log-message {
  color: #1e293b;
  word-break: break-word;
}

.log-level-info .log-level {
  color: #3b82f6;
}

.log-level-warning .log-level {
  color: #f59e0b;
}

.log-level-error .log-level {
  color: #ef4444;
}

.log-level-debug .log-level {
  color: #8b5cf6;
}
</style>
